import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Modal,
  Avatar,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

// Icons
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from "@mui/icons-material/SendRounded";

// Spinner
import Spinner from "../../Component/spinner/spinner";

// Services
import {
  useLazyGetForumByIdQuery,
  useGetApproveRejectForumMutation,
} from "../../app/services/forumServices";
import {
  useSaveCommentsForumMutation,
  useGetAllCommentsForumByIdQuery,
  useUpdateCommentsForumMutation,
} from "../../app/services/forumCommentsServices";

// Selectors
import {
  selectCurrenSigRole,
  selectCurrentSig,
} from "../../app/features/sigSlice";
import {
  selectCurrenChapterRole,
  selectCurrentChapterAdmin,
} from "../../app/features/chapterAdminSlice";
import { selectCurrentMember } from "../../app/features/memberSlice";
import {
  selectCurrentScope,
  selectUserId,
} from "../../app/auth/authSlice";
import ForumPage from "../../Component/ForumPage";


// Modal style
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  p: 3,
};

// Time Ago
const timeAgo = (date) => {
  const diff = Date.now() - new Date(date);
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  return `${seconds} second${seconds > 1 ? "s" : ""} ago`;
};

const ForumDetail = ({ id }) => {
  const { forum } = useParams();
  const forumId = forum || id;
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [activeComment, setActiveComment] = useState(null);
  const [displayedComments, setDisplayedComments] = useState(5);
  const [displayedReplies, setDisplayedReplies] = useState({});
  const [isApprovedData, setIsApprovedData] = useState();
  const [openRejectModal, setOpenRejectModal] = useState(false);

  // Redux state
  const chapterRoleDetails = useSelector(selectCurrenChapterRole);
  const chapterCurrentDetails = useSelector(selectCurrentChapterAdmin);
  const sigRoleDetails = useSelector(selectCurrenSigRole);
  const sigCurrentDetails = useSelector(selectCurrentSig);
  const userId = useSelector(selectUserId);
  const memberDetails = useSelector(selectCurrentMember);
  const memberScope = useSelector(selectCurrentScope);

  // API hooks
  const [getForumById] = useLazyGetForumByIdQuery();
  const [saveCommentsForum] = useSaveCommentsForumMutation();
  const [updateCommentsForum] = useUpdateCommentsForumMutation();
  const { data: getAllCommentsForumById, refetch } =
    useGetAllCommentsForumByIdQuery(forumId, { skip: !forumId });
  const [approveRejectForum] = useGetApproveRejectForumMutation();

  // Forms
  const { register, handleSubmit, reset, trigger } = useForm({
    defaultValues: {
      forumId,
      comment: {
        adminId: "",
        sigId: sigCurrentDetails?._id,
        sigRoleId: sigRoleDetails?._id,
        chapterId: chapterCurrentDetails?._id,
        chapterRoleId: chapterRoleDetails?._id,
        memberId: memberDetails?._id,
        commentContent: "",
      },
    },
  });

  const {
    register: replyRegister,
    handleSubmit: replyHandleSubmit,
    reset: replyReset,
    setValue,
    trigger: replyTrigger,
    watch,
  } = useForm({
    defaultValues: {
      forumId,
      commentId: "",
      reply: {
        adminId: "",
        sigId: sigCurrentDetails?._id,
        sigRoleId: sigRoleDetails?._id,
        chapterId: chapterCurrentDetails?._id,
        chapterRoleId: chapterRoleDetails?._id,
        memberId: memberDetails?._id,
        replyContent: "",
      },
    },
  });

  const {
    register: registerReject,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
    watch: watchReject,
  } = useForm({ defaultValues: { rejectionReason: "" } });

  // Load forum
  useEffect(() => {
    if (forumId) {
      setLoading(true);
      getForumById(forumId)
        .unwrap()
        .then((res) => {
          setPost(res?.data);
          setIsApprovedData(res?.data?.isApproved);
          setLoading(false);
        });
    }
  }, [forumId, getForumById]);

  useEffect(() => {
    if (getAllCommentsForumById?.status) {
      setComments(getAllCommentsForumById.data.comment);
    }
  }, [getAllCommentsForumById]);

  const handleReplyClick = (commentId) => {
    setValue("commentId", commentId);
    setActiveComment((prev) => (prev === commentId ? null : commentId));

    const comment = comments.find((c) => c._id === commentId);
    setDisplayedReplies((prev) => ({
      ...prev,
      [commentId]: comment?.reply?.length > 5 ? 5 : comment?.reply?.length || 0,
    }));
  };

  const onSaveCommentsForum = async (formData) => {
    if (["PORTAL_SUPER_ADMIN", "PORTAL_ADMIN"].includes(memberScope)) {
      formData.comment.adminId = userId;
    }
    const isValid = await trigger();
    if (!isValid) return;

    const mutation =
      comments.length === 0 ? saveCommentsForum : updateCommentsForum;

    const res = await mutation(formData);
    if (res?.data?.status) {
      toast.success(
        comments.length === 0
          ? "Comment Added Successfully!"
          : "Comment Updated Successfully!"
      );
      await refetch().then((res) => setComments(res.data?.data?.comment));
      reset();
    }
  };

  const onSaveReplyForum = async (commentId) => {
    const formData = {
      commentId,
      forumId,
      reply: {
        adminId: ["PORTAL_SUPER_ADMIN", "PORTAL_ADMIN"].includes(memberScope)
          ? userId
          : "",
        sigId: sigCurrentDetails?._id,
        sigRoleId: sigRoleDetails?._id,
        chapterId: chapterCurrentDetails?._id,
        chapterRoleId: chapterRoleDetails?._id,
        memberId: memberDetails?._id,
        replyContent: watch("reply.replyContent"),
      },
    };

    const isValid = await replyTrigger();
    if (!isValid) return;

    const res = await updateCommentsForum(formData);
    if (res?.data?.status) {
      toast.success("Reply Added Successfully!");
      await refetch().then((res) => setComments(res.data?.data?.comment));
      replyReset();
    }
  };

  const handleForumApproveReject = (type) => {
    const rejectionReason = getValues("rejectionReason");
    let payload;

    if (type === 0) {
      payload = { forumId, type };
    } else {
      if (!rejectionReason) {
        setError("rejectionReason", {
          type: "required",
          message: "Please enter a reason for rejection",
        });
        return;
      }
      clearErrors();
      payload = { forumId, type, rejectionReason };
    }

    setLoading(true);
    approveRejectForum(payload).then((res) => {
      setLoading(false);
      if (res.data?.status) {
        if (res.data.data.isApproved === 1) {
          toast.success("Forum Approved Successfully!");
        } else {
          toast.success("Forum Rejected Successfully!");
        }
        window.history.back();
      }
    });
  };

  if (loading) return <Spinner />;
  if (!post) return null;

  return (
    <Box>
      {window.location.pathname.includes("/readMoreForum") && (
        <div className="pt-5 ">
          <div className="chapter-heading">
            <h2 className="text-center title-font text-light py-3 mt-1">
              View Forum
            </h2>
          </div>
        </div>
      )}

      <div className="p-md-5 p-3  overallPadding">
        {window.location.pathname.includes("/readMoreForum") && <div className="text-end mb-3 w-100">
          <Button
            onClick={() => window.history.back()}
            variant="contained"
            className="menus-color"
          >
            Back
          </Button>
        </div>}
        <Box sx={{ maxWidth: "1200px", mx: "auto", paddingInline: { xs: 2, md: 4 } }}>
          {/* Forum Card */}
          <ForumPage forum={post} />
          {isApprovedData === 1 && (
            <>
              <Typography variant="h5" fontWeight="600" mt={4} mb={2}>
                Comments
              </Typography>
              <hr />
            </>
          )}
          {/* Comments */}
          {isApprovedData === 1 && (
            <Box sx={{ mt: 4 }}>
              {/* Comments List */}
              {comments.length > 0 ? (
                comments.slice(0, displayedComments).map((comment) => (
                  <Box
                    key={comment._id}
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "background.default",
                      boxShadow: 1,
                    }}
                  >
                    {/* Comment Header */}
                    <Box className="d-flex justify-content-between align-items-center mb-2">
                      <Box className="d-flex align-items-center">
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 32,
                            height: 32,
                            fontSize: "0.8rem",
                            mr: 1,
                          }}
                        >
                          {comment.commentName?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography fontWeight="600">{comment.commentName}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(comment.commentDate)}
                      </Typography>
                    </Box>

                    {/* Comment Content */}
                    <Typography sx={{ pl: 5, mb: 1 }}>{comment.commentContent}</Typography>

                    {/* Reply Toggle */}
                    <Box className="d-flex justify-content-end">
                      <Button
                        size="small"
                        startIcon={
                          activeComment === comment._id ? (
                            <CloseIcon fontSize="small" />
                          ) : comment.reply?.length > 0 ? (
                            <ChatBubbleOutlineIcon fontSize="small" />
                          ) : (
                            <></>
                            // <ReplyIcon fontSize="small" />
                          )
                        }
                        onClick={() => handleReplyClick(comment._id)}
                      >
                        {activeComment === comment._id
                          ? "Close"
                          : comment.reply?.length > 0
                            ? `${comment.reply.length} Replies`
                            : "Reply"}
                      </Button>
                    </Box>

                    {/* Replies */}
                    {activeComment === comment._id && (
                      <Box sx={{ mt: 2, pl: { xs: 2, md: 6 }, borderLeft: 2, borderColor: "divider" }}>
                        {comment.reply?.slice(0, displayedReplies[comment._id] || 0).map((reply, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              mb: 2,
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: "grey.50",
                              boxShadow: 0,
                            }}
                          >
                            <Box className="d-flex justify-content-between mb-1">
                              <Typography fontWeight="600">{reply.replyName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {timeAgo(reply.replyDate)}
                              </Typography>
                            </Box>
                            <Typography>{reply.replyContent}</Typography>
                          </Box>
                        ))}

                        {/* Reply Input */}
                        <form
                          onSubmit={replyHandleSubmit(() => onSaveReplyForum(comment._id))}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Write a reply..."
                            {...replyRegister("reply.replyContent", { required: true })}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Avatar sx={{ width: 28, height: 28, bgcolor: "secondary.main" }}>
                                    {memberDetails?.name?.charAt(0).toUpperCase()}
                                  </Avatar>
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton type="submit" color="primary">
                                    <SendRoundedIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </form>
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No comments yet. Be the first to comment!</Typography>
              )}

              {/* Add New Comment */}
              <form onSubmit={handleSubmit(onSaveCommentsForum)}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Write a comment..."
                  {...register("comment.commentContent", { required: true })}
                  sx={{ mt: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" className="mt-auto mb-auto">
                        <Avatar sx={{ bgcolor: "secondary.main" }}>
                          {memberDetails?.name?.charAt(0).toUpperCase()}
                        </Avatar>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" className="mt-5">
                        <Button
                          type="submit"
                          variant="text"
                          endIcon={<SendRoundedIcon fontSize="small" />}
                        >
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </form>

              {/* View More */}
              {comments.length > displayedComments && (
                <Box className="text-center mt-3">
                  <Button
                    variant="outlined"
                    onClick={() => setDisplayedComments((c) => c + 5)}
                  >
                    Load More Comments
                  </Button>
                </Box>
              )}
            </Box>
          )}
          {/* Approve / Reject */}
          {isApprovedData === 0 &&
            ["PORTAL_SUPER_ADMIN", "PORTAL_ADMIN"].includes(memberScope) && (
              <Box sx={{ textAlign: "right", marginBlock: 3 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setOpenRejectModal(true)}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  sx={{ ml: 2 }}
                  onClick={() => handleForumApproveReject(0)}
                >
                  Approve
                </Button>
              </Box>
            )}
          {/* Reject Modal */}
          <Modal
            open={openRejectModal}
            onClose={() => setOpenRejectModal(false)}
          >
            <Box sx={modalStyle}>
              <Typography
                variant="h6"
                textAlign="center"
                fontWeight="bold"
                color="error"
                mb={2}
              >
                Reject Forum
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for rejection"
                error={!!errors.rejectionReason}
                helperText={errors.rejectionReason?.message}
                {...registerReject("rejectionReason")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {300 - (watchReject("rejectionReason")?.length || 0)}
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Button onClick={() => setOpenRejectModal(false)} sx={{ mr: 2 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleForumApproveReject(1)}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Modal>
        </Box>
      </div>
    </Box>
  );
};

export default ForumDetail;
